import { useEffect, useState, useCallback, useRef } from 'react';
import { getVisitsByDateRange, visitHours, visitClientName } from '../lib/axiscare';

// Single source of truth for visit data (AxisCare, plus CAERA-native visits
// for any service line switched to CAERA). Fetches the range once, normalizes
// it with one shared status definition, and caches per range for the session.
//
// Each returned visit gets:
//   _status: 'Completed' | 'Scheduled' | 'Missed' | 'Unassigned'
//   _hours:  scheduled duration in hours
//   _standalone: true for a one-off visit (not generated from a recurring schedule)
//
// Rules: a removed visit that has a same-day active replacement is a
// reschedule and is dropped (its replacement is in the list); a removed visit
// with no replacement is a true Missed; duplicate active visits are
// de-duplicated. Open (unassigned) shifts are dropped unless the caller asks
// for them with { includeUnassigned: true } — the calendar wants to show
// coverage gaps, the history list and the money paths do not.

const keyOf = (v) => `${v.client?.id}|${(v.scheduledStartDate || v.startDate || '').slice(0, 10)}`;

// Does a visit belong to this CAERA client? AxisCare id first, name as fallback.
export function matchesClient(v, client) {
  if (!client) return true;
  if (v.client?.caeraId && v.client.caeraId === client.id) return true;
  const ax = client.axiscare_client_id ? String(client.axiscare_client_id) : null;
  if (ax && v.client?.id != null && String(v.client.id) === ax) return true;
  const name = `${client.first_name || ''} ${client.last_name || ''}`.trim().toLowerCase();
  return !!name && visitClientName(v).toLowerCase() === name;
}

export function normalizeVisits(raw, { includeUnassigned = false } = {}) {
  const arr = Array.isArray(raw) ? raw : Object.values(raw || {});
  const activeKeys = new Set(arr.filter(v => !v.removed && v.caregiver).map(keyOf));
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    const standalone = /^v=/.test(String(v.id || v.visitId || ''));
    if (!v.caregiver) {
      if (!includeUnassigned || v.removed) continue;   // open shift (or a removed one)
      out.push({ ...v, _status: 'Unassigned', _hours: visitHours(v), _standalone: standalone });
      continue;
    }
    const k = keyOf(v);
    if (v.removed) {
      if (activeKeys.has(k)) continue;                // rescheduled → superseded
      out.push({ ...v, _status: 'Missed', _hours: visitHours(v), _standalone: standalone });
    } else {
      if (seen.has(k) && !includeUnassigned) continue; // duplicate active visit (list view)
      seen.add(k);
      out.push({ ...v, _status: v.clockIn ? 'Completed' : 'Scheduled', _hours: visitHours(v), _standalone: standalone });
    }
  }
  return out;
}

// Session cache keyed by date range + options.
const cache = new Map();

export default function useVisits({ start, end }, { includeUnassigned = false } = {}) {
  const [state, setState] = useState({ loading: true, error: null, visits: [] });
  const reqRef = useRef(0);

  const fetchRange = useCallback(async (force = false) => {
    if (!start || !end) { setState({ loading: false, error: null, visits: [] }); return; }
    const cacheKey = `${start}|${end}|${includeUnassigned ? 'u' : ''}`;
    if (!force && cache.has(cacheKey)) {
      setState({ loading: false, error: null, visits: cache.get(cacheKey) });
      return;
    }
    const reqId = ++reqRef.current;
    setState(s => ({ ...s, loading: true, error: null }));
    const { data, error } = await getVisitsByDateRange(start, end);
    if (reqId !== reqRef.current) return;             // a newer request superseded this one
    if (error) { setState({ loading: false, error: 'Unable to load visits — check the AxisCare connection.', visits: [] }); return; }
    const visits = normalizeVisits(data?.results?.visits, { includeUnassigned });
    cache.set(cacheKey, visits);
    setState({ loading: false, error: null, visits });
  }, [start, end, includeUnassigned]);

  useEffect(() => { fetchRange(false); }, [fetchRange]);

  const reload = useCallback(() => fetchRange(true), [fetchRange]);
  return { ...state, reload };
}
